import { useLayoutEffect, useMemo, useRef } from 'react'
import { Mic, Download, Loader } from 'lucide-react'
import { useGlobalState } from '@highlight/ipc/react'
import {
  useAudioNoteSavedNote,
  useIsAudioNoteActive,
  useIsAudioNoteReconnecting,
  useIsAudioNoteSaving,
  useIsAudioNoteStarting,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAudioTranscript } from '@renderer/hooks/audio/use-audio-transcript'
import { orderLivePartials, usePartialTranscriptions } from '@renderer/hooks/audio/use-partial-transcriptions'
import { useHeldPartial } from '@renderer/hooks/audio/use-held-partial'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import { DownloadState } from '@shared/ipc/GlobalStateUtils'
import { MeetingTranscript, type TranscriptJump } from '../../../components/meeting/transcript'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '../../../components/meeting/empty-state'

interface LiveTranscriptProps {
  onContentChange: () => void
  /** Returns whether a pin was armed — false while the body's scroller is still null. */
  pinToBottom: () => boolean
  scrollElement: HTMLDivElement | null
  /**
   * Follow new lines by scrolling the view's body. False while the transcript is
   * clipped to a preview (the Brief holds the body): a 96px window has no bottom
   * worth scrolling the whole document to, and doing it anyway would yank the
   * user out of whatever they opened instead.
   */
  autoFollow?: boolean
  jumpTo?: TranscriptJump | null
}

/**
 * Streaming transcript for a live recording: finalized turns with the in-flight
 * line streamed onto the transcript (see `placeLivePartial`). Partial-transcript IPC is
 * subscribed only while this component is mounted.
 */
export function LiveTranscript({
  onContentChange,
  pinToBottom,
  scrollElement,
  autoFollow = true,
  jumpTo,
}: LiveTranscriptProps) {
  const { entries, messages } = useAudioTranscript()
  // Narrow selectors, not `useAudioNoteState()`: main rebroadcasts the whole
  // audio-note-state object with a fresh reference on every transcript/summary
  // tick, and the aggregate hook's useMemo depends on that object — so the
  // hottest component in the app would re-render on every tick.
  const isSaving = useIsAudioNoteSaving()
  const savedNote = useAudioNoteSavedNote()
  const isAudioNoteActive = useIsAudioNoteActive()
  const isReconnecting = useIsAudioNoteReconnecting()
  const partials = usePartialTranscriptions()
  const hasPinnedContentRef = useRef(false)

  const orderedPartials = useMemo(() => orderLivePartials(partials), [partials])

  const heldPartial = useHeldPartial(orderedPartials, entries)
  const showLiveLine = isAudioNoteActive && !isSaving && !savedNote
  const livePartial = showLiveLine && !isReconnecting ? heldPartial : undefined

  // The sidepanel unmounts the whole view tree on close, so every open remounts
  // this against a fresh scroll container *and* an empty transcript: the feed
  // clears its buffers on last release and refetches a snapshot from main, so
  // `messages` is [] for the first few frames and only then jumps to the full
  // backlog. Latch the initial pin on content actually arriving — latching on the
  // first commit leaves the real backlog to be smooth-scrolled into place, which
  // is the "watch it scroll down from the top on open" bug, and the longer the
  // panel was closed the longer that runs. Layout effect so the pin lands before
  // the frame that first paints the backlog; after that, follow new lines as
  // they arrive — instantly, in the same pre-paint pass that inserted them.
  //
  // The latch closes on the pin *landing*, not on the messages being there: the
  // body's scroller reaches `useBodyScroll` through a callback ref, so pinToBottom
  // is a no-op for the whole mount commit. Whenever the feed already holds a
  // backlog on that commit — another consumer kept it alive, so the buffers were
  // never cleared — latching on `messages` alone burns the one pin this open gets
  // without having scrolled anything (HIG-5978).
  const jumpToRef = useRef(jumpTo)
  jumpToRef.current = jumpTo
  useLayoutEffect(() => {
    if (!autoFollow) return
    if (!hasPinnedContentRef.current) {
      // A jump clicked before the backlog arrived owns the first scroll; pinning would undo it and resume following
      if (jumpToRef.current) {
        hasPinnedContentRef.current = true
        return
      }
      const pinned = pinToBottom()
      if (pinned && messages.length > 0) hasPinnedContentRef.current = true
      return
    }
    // Keyed on `messages`, not its length: a speaker relabel regroups rows above
    // a reader without adding one, and holding their place has to see that too.
    onContentChange()
  }, [messages, livePartial?.text, onContentChange, pinToBottom, autoFollow])

  return (
    <MeetingTranscript
      messages={messages}
      livePartial={livePartial}
      virtualize
      scrollElement={scrollElement}
      jumpTo={jumpTo}
      holdsReadingPosition={autoFollow}
      className="overflow-visible"
      emptyState={<LiveTranscriptEmptyState />}
    >
      {showLiveLine && isReconnecting ? (
        <p role="status" className="text-orange-11 type-prose-sm mt-3 select-none font-medium">
          Trying to reconnect…
        </p>
      ) : null}
    </MeetingTranscript>
  )
}

function LiveTranscriptEmptyState() {
  const isStarting = useIsAudioNoteStarting()
  const [yamnetDownloadState] = useGlobalState<DownloadState>('yamnet.download')
  const [whisperDownloadState] = useGlobalState<DownloadState>('whisper.download')
  const { isUsingLocalTranscription } = useAudioNoteSettings()

  const content = useMemo(() => {
    if (yamnetDownloadState?.status === 'downloading') {
      return {
        icon: <Download />,
        title: 'Downloading audio model…',
        description: `${yamnetDownloadState.percent}%. Your transcript starts once it’s ready.`,
      }
    }
    if (whisperDownloadState?.status === 'downloading') {
      return {
        icon: <Download />,
        title: 'Downloading transcription model…',
        description: `${whisperDownloadState.percent}%. Your transcript starts once it’s ready.`,
      }
    }
    if (isStarting) {
      return { icon: <Loader />, title: 'Getting ready…', description: 'Your transcript will appear here.' }
    }
    return {
      icon: <Mic />,
      title: 'Listening…',
      description: isUsingLocalTranscription
        ? 'Your transcript updates every 30 seconds.'
        : 'Chat freely. We’ll focus on the boring stuff.',
    }
  }, [isStarting, yamnetDownloadState, whisperDownloadState, isUsingLocalTranscription])

  return (
    <EmptyState role="status" className="px-5 py-10">
      <EmptyStateIcon>{content.icon}</EmptyStateIcon>
      <EmptyStateTitle>{content.title}</EmptyStateTitle>
      <EmptyStateDescription>{content.description}</EmptyStateDescription>
    </EmptyState>
  )
}
